import React, {useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  z-index: 1200;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(28, 22, 38, 0.12);
  max-height: 220px;
  overflow: auto;
  padding: 4px 0;
`;

const OptionRow = styled.div<{$active: boolean}>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  background: ${({$active}) => ($active ? "#f5f5f5" : "transparent")};

  .code {
    font-weight: 600;
    color: #1c1726;
  }

  .desc {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 2px;
  }

  &:hover {
    background: #fafafa;
  }
`;

export type ShortCodeCompletionItem = {code: string; desc: string};

type ShortCodeCompletionDropdownProps = {
  visible: boolean;
  items: ShortCodeCompletionItem[];
  selectedIndex: number;
  /** Fixed positioning (viewport) */
  anchor: {top: number; left: number; minWidth: number};
  onSelect: (code: string) => void;
  /** Sync keyboard highlight when hovering with mouse */
  onHighlightIndex?: (index: number) => void;
};

/**
 * Portaled listbox under the caret / field; keyboard handled by parent (capture phase).
 */
export function ShortCodeCompletionDropdown({
  visible,
  items,
  selectedIndex,
  anchor,
  onSelect,
  onHighlightIndex,
}: ShortCodeCompletionDropdownProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!visible || items.length === 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-shortcode-index="${selectedIndex}"]`
    );
    el?.scrollIntoView({block: "nearest"});
  }, [visible, items.length, selectedIndex]);

  if (!visible || items.length === 0) return null;

  return createPortal(
    <Overlay
      ref={listRef}
      data-short-code-completion-menu
      role="listbox"
      aria-label="Short code suggestions"
      style={{
        top: anchor.top,
        left: anchor.left,
        minWidth: Math.max(anchor.minWidth, 200),
      }}
    >
      {items.map((item, i) => (
        <OptionRow
          key={item.code}
          role="option"
          aria-selected={i === selectedIndex}
          data-shortcode-index={i}
          $active={i === selectedIndex}
          onMouseEnter={() => onHighlightIndex?.(i)}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(item.code);
          }}
        >
          <div className="code">{item.code}</div>
          <div className="desc">{item.desc}</div>
        </OptionRow>
      ))}
    </Overlay>,
    document.body
  );
}
